import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Github, Code2, Palette, Eye, Calendar, Monitor } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ProjectCardProps {
  title: string;
  summary: string;
  coverImage: string;
  slug: string;
  year: number;
  client?: string;
  disciplines?: string[];
  techStack?: string[];
  projectType: 'design' | 'dev' | 'integrated';
  demoUrl?: string | null;
  githubUrl?: string | null;
  featured?: boolean;
  autoScroll?: boolean;
  index: number;
}

// Hook for animated scroll on hover
const useScrollAnimation = (enabled: boolean, isHovering: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const animate = useCallback((timestamp: number) => {
    if (!containerRef.current) return;

    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const container = containerRef.current;
    const maxScroll = container.scrollHeight - container.clientHeight;

    if (maxScroll <= 0) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    // 8 seconds for one complete scroll cycle
    const duration = 8000;
    const elapsed = timestamp - startTimeRef.current;
    let progress = (elapsed % duration) / duration;

    // Smooth easing at start and end
    if (progress < 0.1) {
      progress = progress * progress * 10;
    } else if (progress > 0.9) {
      const remaining = 1 - progress;
      progress = 1 - (remaining * remaining * 10);
    }

    container.scrollTop = progress * maxScroll;
    animationRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (enabled && isHovering) {
      startTimeRef.current = null;
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [enabled, isHovering, animate]);

  // Reset scroll position when not hovering
  useEffect(() => {
    if (!isHovering && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [isHovering]);

  return containerRef;
};

export const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  summary,
  coverImage,
  slug,
  year,
  client,
  disciplines = [],
  techStack = [],
  projectType,
  demoUrl,
  githubUrl,
  featured = false,
  autoScroll = false,
  index
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const scrollContainerRef = useScrollAnimation(autoScroll, isHovering);

  const isDevProject = projectType === 'dev';
  const TypeIcon = isDevProject ? Code2 : Palette;

  if (featured) {
    return (
      <motion.div
        className="relative w-full h-full min-h-[500px] md:min-h-[600px] lg:min-h-[700px] group overflow-hidden"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: index * 0.1, ease: "easeOut" }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <Link to={`/projetos/${slug}`} className="block w-full h-full">
          <div className="relative w-full h-full bg-gradient-to-br from-slate-900/50 to-slate-800/30 rounded-[2rem] overflow-hidden border border-white/5 group-hover:border-blue-500/30 transition-all duration-500">

            {/* Image container with scroll animation for WEB projects */}
            {autoScroll ? (
              <div
                ref={scrollContainerRef}
                className="absolute inset-0 overflow-hidden"
              >
                <img
                  src={coverImage}
                  alt={title}
                  className={cn(
                    "w-full h-auto min-h-full object-cover object-top transition-transform duration-700 ease-out",
                    isHovering ? "scale-100" : "group-hover:scale-105"
                  )}
                  style={{ filter: 'brightness(0.7) contrast(1.1)' }}
                />
              </div>
            ) : (
              <img
                src={coverImage}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                style={{ filter: 'brightness(0.7) contrast(1.1)' }}
              />
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent opacity-90" />

            <div className="absolute top-6 left-6 z-10 flex gap-2">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Badge className={`px-4 py-2 text-sm font-medium backdrop-blur-md ${isDevProject
                  ? "bg-blue-600/80 text-white border-blue-500/30"
                  : "bg-indigo-600/80 text-white border-indigo-500/30"
                  }`}>
                  <TypeIcon className="w-4 h-4 mr-2" />
                  {isDevProject ? 'Desenvolvimento' : 'Design'}
                </Badge>
              </motion.div>

              {/* WEB Project Badge */}
              {autoScroll && (
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <Badge className="px-4 py-2 text-sm font-medium backdrop-blur-md bg-gradient-to-r from-cyan-600/80 to-blue-600/80 text-white border-cyan-500/30">
                    <Monitor className="w-4 h-4 mr-2" />
                    Projeto WEB
                  </Badge>
                </motion.div>
              )}
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 z-10">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2 text-blue-400">
                    <Eye className="w-5 h-5" />
                    <span className="text-sm font-medium">Destaque</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">{year}</span>
                  </div>
                  {autoScroll && isHovering && (
                    <div className="flex items-center gap-2 text-cyan-400 animate-pulse">
                      <span className="text-sm font-medium">▶ Rolando preview...</span>
                    </div>
                  )}
                </div>

                <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors">
                  {title}
                </h3>

                <p className="text-lg md:text-xl text-gray-300 mb-6 line-clamp-2 max-w-2xl">
                  {summary}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {isDevProject && techStack.length > 0 ? (
                    techStack.slice(0, 4).map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1.5 text-sm bg-blue-500/20 text-blue-300 backdrop-blur-sm rounded-lg border border-blue-500/20"
                      >
                        {tech}
                      </span>
                    ))
                  ) : (
                    disciplines.slice(0, 3).map((discipline) => (
                      <span
                        key={discipline}
                        className="px-3 py-1.5 text-sm bg-indigo-500/20 text-indigo-300 backdrop-blur-sm rounded-lg border border-indigo-500/20"
                      >
                        {discipline}
                      </span>
                    ))
                  )}
                </div>

                {isDevProject && (demoUrl || githubUrl) && (
                  <div className="flex gap-3 pt-4 border-t border-white/10">
                    {demoUrl && (
                      <a
                        href={demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span className="text-sm font-medium">Demo</span>
                      </a>
                    )}
                    {githubUrl && (
                      <a
                        href={githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Github className="w-4 h-4" />
                        <span className="text-sm font-medium">Código</span>
                      </a>
                    )}
                  </div>
                )}

                {!isDevProject && (
                  <div className="flex items-center gap-2 text-blue-400 group-hover:gap-3 transition-all duration-300">
                    <span className="text-base font-medium">Ver projeto completo</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  // Regular (non-featured) card
  return (
    <motion.div
      className="relative w-full bg-gradient-to-br from-slate-900/50 to-slate-800/30 rounded-2xl overflow-hidden border border-white/5 group"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: "easeOut" }}
      whileHover={{
        y: -5,
        borderColor: "rgba(59, 130, 246, 0.3)",
        boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.15)"
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Link to={`/projetos/${slug}`} className="block w-full">
        <div className="aspect-[4/3] overflow-hidden relative">
          {/* Image with scroll animation for WEB projects */}
          {autoScroll ? (
            <div
              ref={scrollContainerRef}
              className="absolute inset-0 overflow-hidden"
            >
              <img
                src={coverImage}
                alt={title}
                className={cn(
                  "w-full h-auto min-h-full object-cover object-top",
                  !isHovering && "group-hover:scale-105 transition-transform duration-500 ease-out"
                )}
                style={{ filter: 'brightness(0.8) contrast(1.1)' }}
              />
            </div>
          ) : (
            <img
              src={coverImage}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
              style={{ filter: 'brightness(0.8) contrast(1.1)' }}
            />
          )}

          <div className="absolute top-4 left-4 flex gap-2">
            <Badge className={`px-3 py-1.5 text-xs font-medium backdrop-blur-md ${isDevProject
              ? "bg-blue-600/80 text-white border-blue-500/30"
              : "bg-indigo-600/80 text-white border-indigo-500/30"
              }`}>
              <TypeIcon className="w-3 h-3 mr-1.5" />
              {isDevProject ? 'Dev' : 'Design'}
            </Badge>

            {/* WEB Project indicator */}
            {autoScroll && (
              <Badge className="px-3 py-1.5 text-xs font-medium backdrop-blur-md bg-gradient-to-r from-cyan-600/80 to-blue-600/80 text-white border-cyan-500/30">
                <Monitor className="w-3 h-3 mr-1.5" />
                WEB
              </Badge>
            )}
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-60" />

          {/* Scroll animation indicator */}
          {autoScroll && isHovering && (
            <div className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-xs text-cyan-400 font-medium animate-pulse">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-ping" />
              Rolando...
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-400">{client}</span>
            <span className="text-xs text-gray-500">{year}</span>
          </div>

          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
            {title}
          </h3>

          <p className="text-gray-400 text-sm leading-relaxed line-clamp-2 mb-4">
            {summary}
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            {isDevProject && techStack.length > 0 ? (
              techStack.slice(0, 3).map((tech) => (
                <span
                  key={tech}
                  className="px-2 py-1 text-xs bg-blue-500/10 text-blue-400 rounded-md border border-blue-500/10"
                >
                  {tech}
                </span>
              ))
            ) : (
              disciplines.slice(0, 3).map((discipline) => (
                <span
                  key={discipline}
                  className="px-2 py-1 text-xs bg-indigo-500/10 text-indigo-400 rounded-md border border-indigo-500/10"
                >
                  {discipline}
                </span>
              ))
            )}
          </div>

          <div className="flex gap-2 pt-4 border-t border-white/5">
            {isDevProject && demoUrl && (
              <a
                href={demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3 h-3" />
                Demo
              </a>
            )}
            {isDevProject && githubUrl && (
              <a
                href={githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Github className="w-3 h-3" />
                Código
              </a>
            )}
            {!isDevProject && (
              <div className="flex items-center gap-1 text-blue-400 ml-auto text-sm">
                Ver <ArrowRight className="w-4 h-4" />
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
